/**
 * Geometry-layer **visibility**, sourced from the active Profile's
 * ``map.visible_layers`` (ADR-033 §9.6; REQ-UI-089) — the single source of
 * truth for whether the Map's three SAPIENT geometry polygon overlays
 * (field-of-view / coverage / obscuration) render.
 *
 * # Single source: the working state (post-inspection item 3a)
 *
 * Visibility used to layer a per-browser ``localStorage`` *session override*
 * (toggled from the map-panel settings cog) **on top of** the Profile
 * default. The map cog menu was removed in the post-inspection pass (item
 * 3a) because every control it carried is reachable in the Profile editor's
 * Map section — and with the cog gone nothing wrote the session override
 * anymore. So the override layer (and its localStorage / cross-tab
 * machinery) is gone: the effective visibility is now simply the active
 * Profile's ``map.visible_layers.{fov,coverage,obscuration}``, edited via the
 * Profile editor's "Layer visibility" controls (REQ-UI-077). A fresh working
 * state with no slice falls back to ``false`` (off-by-default per
 * REQ-UI-070, preserving the dense-overlay-hiding baseline).
 *
 * ## ``visible_layers`` vs ``map.platform.geometry_defaults``
 *
 * Today's geometry visibility is a single **global** layer flag (one
 * ``fovVisible`` for *all* platforms), so it reads from ``map.visible_layers``
 * — the "toggleable map layers" field (ADR-033 §9.6). The schema's separate
 * **per-sensor** ``map.platform.geometry_defaults`` (§9.3) is forward-looking:
 * it would drive per-sensor geometry visibility once a per-sensor toggle
 * exists. There is no per-sensor toggle today, so ``geometry_defaults`` is
 * intentionally left **unconsumed** here.
 *
 * Cross-references:
 *   - REQ-UI-070 (SAPIENT map geometry — off-by-default baseline).
 *   - REQ-UI-077 (Profile-editor layer-visibility controls).
 *   - REQ-UI-089 (geometry-layer visibility default from the Profile).
 *   - REQ-UI-027 (client-state ownership).
 *   - ADR-032 §4.2 (Status geometry data model); ADR-033 §9.3 / §9.6.
 *   - map/fov-layer.tsx · map/coverage-layer.tsx · map/obscuration-layer.tsx
 *     (the three consumers).
 */
import { useContext, useMemo } from "react";
import type { components } from "@/lib/api-types.gen";
import { PreferencesContext } from "@/preferences/preferences-context";

/**
 * The Profile's toggleable-layers slice (``map.visible_layers``; ADR-033
 * §9.6) as the BFF serialises it. Re-exported from the generated **output**
 * schema so the mapping below stays in lock-step with the wire (CLAUDE.md
 * §5.11 — generated types only). Every field is required in the schema but
 * the whole slice is optional on ``map``, so callers pass
 * ``workingState.map?.visible_layers`` (possibly ``undefined``).
 */
export type ProfileVisibleLayers = components["schemas"]["VisibleLayers"];

/**
 * Project the Profile's ``map.visible_layers`` onto the three geometry-layer
 * flags this module consumes (REQ-UI-089; ADR-033 §9.6).
 *
 * Only the three geometry flags (``fov`` / ``coverage`` / ``obscuration``)
 * map onto a layer this module gates today. ``trails`` / ``detections`` /
 * ``alerts`` are deliberately **NOT** projected — those layers are
 * always-rendered with no visibility gate, so there is nowhere to apply
 * their Profile flag yet. REQ-UI-089 covers them once such a gate exists;
 * until then their ``visible_layers`` entries are intentionally unconsumed.
 *
 * @param visible - ``workingState.map?.visible_layers``, or ``undefined``.
 * @returns Geometry flags for :func:`useGeometryVisibility`. An absent slice
 *     yields ``{}`` (every flag falls through to ``false``).
 */
export function geometryDefaultsFromProfile(
  visible: ProfileVisibleLayers | undefined,
): GeometryProfileDefaults {
  if (visible === undefined) {
    return {};
  }
  return {
    fovVisible: visible.fov,
    coverageVisible: visible.coverage,
    obscurationVisible: visible.obscuration,
    // NOTE (REQ-UI-089): visible.trails / visible.detections /
    // visible.alerts intentionally omitted — those layers have no visibility
    // gate today (always-rendered), so there's no consumer for their Profile
    // flag. Wire them here once a gate exists.
  };
}

/**
 * Read the active Profile's geometry-layer flags from the preferences
 * context (REQ-UI-089; ADR-033 §9.6) — the partial (each flag possibly
 * ``undefined``) that :func:`useGeometryVisibility` resolves against the
 * off-by-default floor.
 *
 * Reads :data:`PreferencesContext` directly (NOT via :func:`usePreferences`)
 * so it **returns ``{}`` when there is no provider** rather than throwing.
 * That non-throwing posture is deliberate: the three geometry layer
 * components (:class:`FovLayer` / :class:`CoverageLayer` /
 * :class:`ObscurationLayer`) can be unit-tested by rendering them bare
 * against a fake map with no surrounding provider; an absent provider must
 * degrade to the all-``false`` baseline (REQ-UI-070), not crash. When a
 * provider IS present (the live app + the integration tests) the active
 * Profile's ``map.visible_layers`` flags flow through.
 *
 * @returns The geometry flags, or ``{}`` when no provider / no
 *     ``visible_layers`` slice is in scope.
 */
export function useGeometryProfileDefaults(): GeometryProfileDefaults {
  const ctx = useContext(PreferencesContext);
  const visible = ctx?.workingState.map?.visible_layers;
  // Memoise on the three source flags so a parent re-render doesn't hand the
  // resolver a fresh object literal every time (keeps the resolved config's
  // identity stable for the layers' effect deps). The memo body reads ONLY
  // these three primitives — not ``visible`` (a fresh object each render) —
  // so the dependency array is exhaustive without a lint suppression.
  const fov = visible?.fov;
  const coverage = visible?.coverage;
  const obscuration = visible?.obscuration;
  return useMemo<GeometryProfileDefaults>(
    () => ({ fovVisible: fov, coverageVisible: coverage, obscurationVisible: obscuration }),
    [fov, coverage, obscuration],
  );
}

/**
 * Effective visibility config for the three SAPIENT geometry layers. All
 * default ``false`` (off-by-default per REQ-UI-070).
 */
export interface GeometryLayersConfig {
  /** Field-of-view polygon layer visible. */
  fovVisible: boolean;
  /** Coverage polygon layer visible. */
  coverageVisible: boolean;
  /** Obscuration polygon layer visible. */
  obscurationVisible: boolean;
}

/** Off-by-default config — every geometry layer hidden (REQ-UI-070). The
 *  floor under the Profile flags. */
export const GEOMETRY_LAYERS_DEFAULTS: GeometryLayersConfig = {
  fovVisible: false,
  coverageVisible: false,
  obscurationVisible: false,
};

/**
 * The Profile-supplied visibility for the three geometry layers (REQ-UI-089)
 * — built directly from the active Profile's ``map.visible_layers`` (ADR-033
 * §9.6). Every field is optional so a partial / absent ``visible_layers``
 * degrades cleanly to ``false`` per flag.
 *
 * This is the **global** geometry visibility. The per-sensor
 * ``map.platform.geometry_defaults`` (§9.3) is a separate, forward-looking
 * field with no consumer today (no per-sensor toggle exists).
 */
export interface GeometryProfileDefaults {
  /** Profile visibility for the field-of-view layer (``visible_layers.fov``). */
  fovVisible?: boolean;
  /** Profile visibility for the coverage layer (``visible_layers.coverage``). */
  coverageVisible?: boolean;
  /** Profile visibility for the obscuration layer
   *  (``visible_layers.obscuration``). */
  obscurationVisible?: boolean;
}

/**
 * Resolve the effective geometry-layer visibility from the active Profile's
 * ``map.visible_layers`` (REQ-UI-089), coercing each absent flag to the
 * off-by-default ``false`` floor (REQ-UI-070).
 *
 * This is the single source of truth the three geometry layer components
 * read — there is no per-session override layer anymore (item 3a; see the
 * module docstring). A Profile Apply that changes ``map.visible_layers``
 * re-renders the layers because :func:`useGeometryProfileDefaults` reads the
 * live working state.
 *
 * @returns The effective ``{ fovVisible, coverageVisible, obscurationVisible }``
 *     — all ``false`` when no provider / no ``visible_layers`` is in scope.
 */
export function useGeometryVisibility(): GeometryLayersConfig {
  const defaults = useGeometryProfileDefaults();
  const fov = defaults.fovVisible;
  const coverage = defaults.coverageVisible;
  const obscuration = defaults.obscurationVisible;
  return useMemo<GeometryLayersConfig>(
    () => ({
      fovVisible: fov ?? false,
      coverageVisible: coverage ?? false,
      obscurationVisible: obscuration ?? false,
    }),
    [fov, coverage, obscuration],
  );
}
